import ReplyIcon from '@mui/icons-material/Reply'
import axios from 'axios'
import dayjs from 'dayjs'
import { useContext, useRef, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import img from 'src/assets/coming_up.png'
import { LocalizationContext } from 'src/consumer/context/LocalizationContext'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from 'src/consumer/context/SetupContext'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import { getFlagEmoji, profileOptionsList } from 'src/consumer/helpers/profilePageHelper'
import { apiURL, commonHeadersWeb, mediaURL } from 'src/consumer/services'
import { logoutHandler } from 'src/consumer/services/loginLogoutHandlers'
import { getData, postData } from 'src/consumer/services/profileService'
import { DailyPlaySettingsComponent, cleanDataFunction } from '../DailyPlay/DailyPlayComponents'
import About from './About'
import {
  ProfileContainer,
  ProfilePageContentsLandscape,
  ProfilePageContentsPortrait,
  ProfilePageLeftContent,
  ProfilePagePortraitLowerContainer,
  ProfilePagePortraitUpperContainer,
  ProfilePageRightContent,
  StickyMenu,
  StickyMenuOption,
  StickyMenuOptionIcon,
  StyledDefaultPage,
  StyledDefaultText,
} from './ConsumerProfileStyle'
import CreditsAndPurchases from './CreditsAndPurchases'
import Goals from './Goals'
import ProfileLeftPannel from './ProfileLeftPannel'
import {
  BackHomeText,
  CameraIcon,
  DisplayFlex,
  IndeterminateLoading,
  LogoutForMobile,
  MembershipStatusBox,
  MembershipText,
  ProfileId,
  ProfileIntitial,
  ProfileMembership,
  ProfileName,
  ProfilePicture,
  ProfileUpgradeButton,
} from './ProfileLeftPannelStyle'
import YourProfile from './YourProfile'
import { isValidHttpUrl } from 'src/mentor/helpers/utilityFunctions'
import isEqual from 'lodash/isEqual'
import logoutIcon from 'src/assets/logout_icon.png'
import uploadCloudIcon from 'src/assets/upload_cloud.png'
import pencilIcon from 'src/assets/pencil.png'
import { CameraAndMicrophoneHelp } from './CameraAndMicrophoneHelp'

const renderProfileDetails = (
  selectedProfileOption,
  localizedStrings,
  saveDailyPlaySettings,
  dailyPlayContentTypesSectionWise,
  userPlayConfig,
  setUserPlayConfig,
  timeSlots,
  setupData,
) => {
  switch (selectedProfileOption) {
    case 'your_profile':
      return <YourProfile localizedStrings={localizedStrings} />
    case 'goals':
      return <Goals localizedStrings={localizedStrings} />
    case 'about':
      return <About localizedStrings={localizedStrings} />
    case 'daily_checkin_preferences':
      return (
        <DailyPlaySettingsComponent
          closeFunction={saveDailyPlaySettings}
          dailyPlayContentTypesSectionWise={dailyPlayContentTypesSectionWise}
          userPlayConfig={userPlayConfig}
          setUserPlayConfig={setUserPlayConfig}
          playTypes={setupData?.metaPlayConfig?.playTypes}
          timeSlotsData={timeSlots}
          renderedInProfileSettings={true}
        />
      )
    case 'your_credits_and_purchases':
      return <CreditsAndPurchases localizedStrings={localizedStrings} />
    case 'camera_microphone_help':
      return <CameraAndMicrophoneHelp />
    case 'manage_data_and_account':
      return (
        <div style={{ color: '#FFF', padding: '5%' }}>
          To understand and manage how AUMHUM manages your data,{' '}
          <Link to={'/manage-data-and-account'} style={{ color: '#FFF' }}>
            click here
          </Link>
        </div>
      )

    default:
      return (
        <StyledDefaultPage>
          <img src={img} alt="coming soon" />
          <StyledDefaultText>Coming Soon</StyledDefaultText>
        </StyledDefaultPage>
      )
  }
}

const ConsumerProfilePage = () => {
  const pageId = 'profilePage'
  const languageData = useContext(LocalizationContext)

  const { localizedDataLoading, localizedData, localizedDataError } =
    useContext(LocalizationContext)
  const localizedStrings = localizedData?.[pageId]

  const { unSecureSetupLoading, unSecureSetupData, unSecureSetupError } =
    useContext(UnsecureSetupContext)
  const [selectedProfileOption, setProfileOption] = useState('your_profile')

  const navigate = useNavigate()

  const fileInputRef = useRef(null)
  const [tempImgUrl, setTempImgUrl] = useState()
  const [waitingPictureUpload, setWaitingPictureUpload] = useState(false)
  const { profileData, setProfileData } = useContext(ProfileContext)
  const { name, email, subscriptionType, profilePicId, location, subscriptionEndDate } = profileData
  const isFreeUser = profileData?.subscriptionTypeId === 'subscription_free'

  const [userPlayConfig, setUserPlayConfig] = useState(profileData?.playConfig || {})
  const outletContext = useOutletContext()
  const { timeSlotsData: timeSlots } = outletContext
  const { setupData } = useContext(SetupContext)

  const { openPrewallFunction } = useOutletContext()

  const handleClick = () => {
    fileInputRef?.current?.click()
  }

  const handleFileUpload = async (event) => {
    const file = event?.target?.files[0] //getting the file

    const reader = new FileReader()
    reader.onload = () => {
      setTempImgUrl(reader.result)
    }
    reader.readAsDataURL(file) //for showing image without waiting for api response

    const formData = new FormData()
    formData?.append('file', file)

    setWaitingPictureUpload(true)

    try {
      const response = await axios.post(`${apiURL}/api/media/file/upload`, formData, {
        headers: {
          ...commonHeadersWeb,
          'ef-user-country-code': localStorage.getItem('countryCode'),
          'ef-app-language': sessionStorage.getItem('languageCode'),
        },
      })

      if (response?.status === 200) {
        let profileUpdatePayload = profileData
        profileUpdatePayload['profilePicId'] = response?.data?.fileId

        const updateResponse = await postData('/api/secure/user/update', profileUpdatePayload)

        if (updateResponse?.status === 200) {
          setWaitingPictureUpload(false)
          const resp = await getData('/api/secure/user/profile')
          if (resp?.status === 200) {
            setProfileData(resp?.data)
          }
        } else {
          setTempImgUrl(null)
          setWaitingPictureUpload(false)
        }
      } else {
        setTempImgUrl(null)
        setWaitingPictureUpload(false)
      }
    } catch (error) {
      console.log(error)
      setTempImgUrl(null)
      setWaitingPictureUpload(false)
    }
    // uploading image, updating profile and updating profile data
  }

  // this function is duplicated from daily play index. should be later put in same place
  const saveDailyPlaySettings = async () => {
    await postData('/api/secure/user/play-preferences/save', userPlayConfig)
    if (!isEqual(userPlayConfig, profileData?.playConfig)) {
      window.location.reload()
    }
    // const resp = await getData('/api/secure/user/profile')
    // if (resp?.status === 200) {
    //     setProfileData(resp?.data)
    // }
  }
  // for daily play settings

  const dailyPlayContentTypes = cleanDataFunction(setupData?.metaPlayConfig?.contentTypes, 'id')
  const dailyPlayContentTypesSectionWise = Object.values(
    dailyPlayContentTypes?.reduce((acc, item) => {
      item?.sectionIds?.forEach((sectionId) => {
        if (!acc[sectionId]) {
          acc[sectionId] = { sectionId, items: [] }
        }
        acc[sectionId].items.push(item)
      })
      return acc
    }, {}),
  )

  return (
    <ProfileContainer>
      {/* portrait layout */}
      <ProfilePageContentsPortrait isMobile={unSecureSetupData?.clientType === 'mobileBrowser'}>
        <ProfilePagePortraitUpperContainer>
          <BackHomeText isMobile={unSecureSetupData?.clientType === 'mobileBrowser'}>
            <DisplayFlex onClick={() => navigate('/')}>
              <ReplyIcon sx={{ fontSize: 15, color: 'white' }} />
              <div>Back to Homepage</div>
            </DisplayFlex>
            <LogoutForMobile
              onClick={() => {
                logoutHandler()
                navigate('/login')
              }}
            >
              <StickyMenuOptionIcon src={logoutIcon} />
              Logout
            </LogoutForMobile>
          </BackHomeText>

          {profilePicId || tempImgUrl ? (
            <ProfilePicture
              imgURL={
                tempImgUrl ||
                (isValidHttpUrl(profilePicId) ? profilePicId : mediaURL + profilePicId)
              }
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <CameraIcon src={uploadCloudIcon} onClick={handleClick} />
              {waitingPictureUpload && <IndeterminateLoading />}
            </ProfilePicture>
          ) : (
            <ProfileIntitial>
              {name[0] || ''}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              <CameraIcon src={pencilIcon} onClick={handleClick} />
            </ProfileIntitial>
          )}

          <ProfileName>{name}</ProfileName>

          <ProfileId>{email}</ProfileId>

          <ProfileId>
            {location?.country}&nbsp;
            {getFlagEmoji(location?.countryCode || '')}
          </ProfileId>

          <MembershipStatusBox>
            <MembershipText>
              Membership
              <ProfileMembership>{subscriptionType?.name}</ProfileMembership>
              {subscriptionType?.id === 'subscription_normal' && (
                <div>Valid till {dayjs.unix(subscriptionEndDate).format('MMMM D, YYYY')}</div>
              )}
            </MembershipText>

            {isFreeUser && (
              <ProfileUpgradeButton onClick={() => openPrewallFunction('upgrade_btn')}>
                Upgrade
              </ProfileUpgradeButton>
            )}
          </MembershipStatusBox>
        </ProfilePagePortraitUpperContainer>

        <ProfilePagePortraitLowerContainer>
          <StickyMenu isMobile={unSecureSetupData?.clientType === 'mobileBrowser'}>
            {profileOptionsList?.map((option) => {
              if (option?.id === 'logout') {
                return null
              }
              const IconComponent = option?.icon
              return (
                <StickyMenuOption
                  onClick={() => {
                    setProfileOption(option?.id)
                  }}
                  isSelected={selectedProfileOption === option?.id}
                  key={option?.id}
                  optionId={option?.id}
                >
                  {option?.isMuiIcon ? (
                    <IconComponent sx={{ width: 24, height: 24, marginRight: 1 }} />
                  ) : (
                    <StickyMenuOptionIcon src={option?.icon} />
                  )}
                  {localizedStrings?.[option?.id] || option?.displayName}
                </StickyMenuOption>
              )
            })}
          </StickyMenu>

          {renderProfileDetails(
            selectedProfileOption,
            localizedStrings,
            saveDailyPlaySettings,
            dailyPlayContentTypesSectionWise,
            userPlayConfig,
            setUserPlayConfig,
            timeSlots,
            setupData,
          )}
        </ProfilePagePortraitLowerContainer>
      </ProfilePageContentsPortrait>

      {/* landscape layout */}
      <ProfilePageContentsLandscape isMobile={unSecureSetupData?.clientType === 'mobileBrowser'}>
        <ProfilePageLeftContent>
          <ProfileLeftPannel
            selectedProfileOption={selectedProfileOption}
            setProfileOption={setProfileOption}
            localizedStrings={localizedStrings}
          />
        </ProfilePageLeftContent>

        <ProfilePageRightContent>
          {renderProfileDetails(
            selectedProfileOption,
            localizedStrings,
            saveDailyPlaySettings,
            dailyPlayContentTypesSectionWise,
            userPlayConfig,
            setUserPlayConfig,
            timeSlots,
            setupData,
          )}
        </ProfilePageRightContent>
      </ProfilePageContentsLandscape>
    </ProfileContainer>
  )
}

export default ConsumerProfilePage
